import React, { useEffect } from "react";
import { User, LogOut } from "lucide-react";
import PremiumHeader from "../components/ui/PremiumHeader";
import MembershipStatsCard from "../components/ui/MembershipStatsCard";
import HistoryEventCard from "../components/ui/HistoryEventCard";
import { useAuth } from "../hooks";
import { useMypageContext } from "../context/MypageContext";
import { UI_TEXT } from "../constants/uiText";
import type { MypageEventHistory } from "../types/swagger";
import { getJstYearLabel } from "../utils/eventSections";

interface ProfileViewProps {
  onLogout: () => void;
  onNotificationClick: () => void;
}

const groupEventHistoryByYear = (eventHistory: MypageEventHistory[]) => {
  const sortedHistory = [...eventHistory].sort(
    (left, right) =>
      new Date(right.event_date ?? "").getTime() -
      new Date(left.event_date ?? "").getTime(),
  );

  return sortedHistory.reduce<
    Array<{ year: string; events: MypageEventHistory[] }>
  >((groups, event) => {
    const year = getJstYearLabel(event.event_date ?? "");
    const currentGroup = groups[groups.length - 1];

    if (!currentGroup || currentGroup.year !== year) {
      groups.push({ year, events: [event] });
      return groups;
    }

    currentGroup.events.push(event);
    return groups;
  }, []);
};

const ProfileView: React.FC<ProfileViewProps> = ({
  onLogout,
  onNotificationClick,
}) => {
  const { mypage, isLoading, error, refresh } = useMypageContext();
  const { logout } = useAuth();

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const eventHistoryByYear = groupEventHistoryByYear(
    mypage?.event_history ?? [],
  );

  const handleLogout = async () => {
    await logout();
    onLogout();
  };

  return (
    <div className="flex flex-col h-full animate-in fade-in duration-300 overflow-hidden">
      <PremiumHeader
        title={UI_TEXT.profile.title}
        icon={User}
        theme="emerald"
        onNotificationClick={onNotificationClick}
      />
      <div className="px-6 py-6 pb-40 overflow-y-auto scrollbar-hide text-center flex-1">
        <MembershipStatsCard
          points={mypage?.yearly_point ?? "-"}
          rank={mypage?.yearly_rank ?? "-"}
          loginId={mypage?.login_id}
          dartsliveName={mypage?.dartslive_card_name}
          eventGroupName={mypage?.event_group?.name}
          theme="emerald"
        />
        <button
          onClick={handleLogout}
          className="w-full bg-white border border-slate-200 text-slate-600 font-bold tracking-wider uppercase rounded-2xl py-4 flex items-center justify-center gap-2 shadow-sm active:scale-95 mb-10 transition-all hover:bg-rose-50 hover:text-rose-600"
        >
          <LogOut className="w-5 h-5" /> {UI_TEXT.profile.logout}
        </button>
        <h2 className="text-[11px] font-black text-slate-400 uppercase tracking-widest mb-4 pl-1 text-left">
          {UI_TEXT.profile.matchHistory}
        </h2>
        {isLoading && (
          <p className="text-slate-400 text-xs font-bold text-center py-8">
            {UI_TEXT.common.loading}
          </p>
        )}
        {error && (
          <p className="text-rose-500 text-xs font-bold text-center py-8">
            {error}
          </p>
        )}
        {!isLoading &&
          !error &&
          eventHistoryByYear.map(({ year, events }) => (
            <section key={year} className="mb-8 text-left last:mb-0">
              <h3 className="text-[11px] font-black text-slate-400 uppercase tracking-widest mb-4 pl-1">
                {`${year}`}
              </h3>
              {events.map((event, index) => (
                <HistoryEventCard
                  key={`${year}-${event.event_name}-${event.event_date}-${index}`}
                  title={event.event_name ?? ""}
                  date={event.event_date ?? ""}
                  rank={event.singles_rank ?? 0}
                  points={event.singles_point ?? 0}
                  eventGroupName={event.event_group?.name}
                />
              ))}
            </section>
          ))}
      </div>
    </div>
  );
};

export default ProfileView;
